import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import styled from 'styled-components';

const FilterContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: ${(p) => (p.$inlineToolbar ? '0' : '0.5rem')};
  min-width: 0;

  label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--color-text-secondary);
    margin-bottom: 0.1rem;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.75rem;
  border: none;
  border-radius: 12px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 180px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-primary);
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }

  &:hover {
    border-color: var(--color-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    box-shadow: none;
  }
`;

const DropdownIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  flex-shrink: 0;
  cursor: pointer;
  pointer-events: auto;
`;

const DropdownMenu = styled.div`
  position: fixed;
  z-index: 10000;
  min-width: 220px;
  max-width: min(320px, calc(100vw - 1rem));
  display: flex;
  flex-direction: column;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
  overflow: hidden;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.5rem 0.25rem;
  max-height: min(240px, 42vh);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    max-height: min(280px, 48vh);
  }
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--color-text-primary);
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-bg-secondary);
  }

  input {
    accent-color: var(--color-text-secondary);
  }
`;

const MenuFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  min-height: 2.75rem;
  padding: 0.55rem 0.65rem 0.65rem;
  border-top: 1px solid var(--color-border-primary);
  background: var(--color-bg-primary);
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #d63031;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.45rem 0.65rem;
  line-height: 1.3;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #adb5bd;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const FieldFilter = ({
  selectedValues,
  onValuesChange,
  items,
  disabled = false,
  label = 'Campo:',
  fieldId = 'field-filter',
  maxSelections,
  emptyMessage = 'Nenhuma opção disponível',
  inlineToolbar = false,
}) => {
  const currentValues = Array.isArray(selectedValues) ? selectedValues : [];
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;

    const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    if (left < 8) left = 8;

    if (openUpward) {
      setMenuStyle({
        left,
        width,
        bottom: window.innerHeight - rect.top + gap,
        top: 'auto',
      });
    } else {
      setMenuStyle({
        left,
        width,
        top: rect.bottom + gap,
        bottom: 'auto',
      });
    }
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleOptionToggle = (value) => {
    onValuesChange((prev) => {
      const prevValues = Array.isArray(prev) ? prev : [];
      const isSelected = prevValues.includes(value);

      if (maxSelections === 1) {
        return isSelected ? [] : [value];
      }

      return isSelected
        ? prevValues.filter((v) => v !== value)
        : [...prevValues, value];
    });
  };

  const handleClearAll = () => {
    onValuesChange(() => []);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();

    const handleClickOutside = (event) => {
      const inButton = buttonRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inButton && !inMenu) {
        setIsOpen(false);
      }
    };

    const handleReposition = () => updateMenuPosition();

    const timeoutId = window.setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const summaryLabel = currentValues.length
    ? `${currentValues.length} selecionado${currentValues.length > 1 ? 's' : ''}`
    : 'Nenhum filtro aplicado';

  const menu = isOpen
    ? createPortal(
        <DropdownMenu ref={menuRef} style={menuStyle} role="listbox" aria-labelledby={fieldId}>
          <OptionsList>
            {items && items.length > 0 ? (
              items.map((opt) => (
                <OptionRow key={opt.value}>
                  <input
                    type="checkbox"
                    value={opt.value}
                    checked={currentValues.includes(opt.value)}
                    onChange={() => handleOptionToggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </OptionRow>
              ))
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                {emptyMessage}
              </span>
            )}
          </OptionsList>

          <MenuFooter>
            <ClearButton
              type="button"
              onClick={handleClearAll}
              disabled={currentValues.length === 0}
            >
              Limpar tudo
            </ClearButton>
          </MenuFooter>
        </DropdownMenu>,
        document.body
      )
    : null;

  return (
    <FilterContainer ref={containerRef} $inlineToolbar={inlineToolbar}>
      <label htmlFor={fieldId}>{label}</label>
      <DropdownButton
        ref={buttonRef}
        id={fieldId}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{summaryLabel}</span>
        <DropdownIcon
          aria-hidden
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            setIsOpen((prev) => !prev);
          }}
        >
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </DropdownIcon>
      </DropdownButton>
      {menu}
    </FilterContainer>
  );
};

FieldFilter.propTypes = {
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  onValuesChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  fieldId: PropTypes.string,
  maxSelections: PropTypes.number,
  emptyMessage: PropTypes.string,
  inlineToolbar: PropTypes.bool,
};

export default FieldFilter;
